import React, { useEffect, useState } from "react";
import axios from "axios";
import { Spin } from "antd";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import "../tester-teamlead/statsModalContent.css";
import "./appwiseCountV2.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const apiUrl =
  "https://j8pcg4bmwc.execute-api.us-east-1.amazonaws.com/dev/frequently-used-apps";

const toMidnightUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const AppWiseCountV2 = () => {
  const [data, setData] = useState([]);
  const [selectedRange, setSelectedRange] = useState([new Date(), new Date()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedRange.length !== 2) return;
      setLoading(true);
      try {
        const startDate = toMidnightUTC(new Date(selectedRange[0]));
        const endDate = toMidnightUTC(new Date(selectedRange[1]));

        const response = await axios.post(apiUrl, {
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        });

        const otpCount = response.data?.otp_count || {};
        const flattenedData = [];
        Object.entries(otpCount).forEach(([date, appCounts]) => {
          Object.entries(appCounts || {}).forEach(([app, count]) => {
            flattenedData.push({ date, app, count });
          });
        });
        setData(flattenedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedRange]);

  const handleDateChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    setSelectedRange([startDate, endDate]);
  };

  const groupedData = data.reduce((acc, curr) => {
    acc[curr.date] = acc[curr.date] || {};
    acc[curr.date][curr.app] = curr.count;
    return acc;
  }, {});

  const dates = Object.keys(groupedData).sort();
  const apps = Array.from(new Set(data.map((item) => item.app))).sort();
  const appColors = {
    WhatsApp: "#22CBCC",
    Facebook: "#8b5cf6",
    Instagram: "#f59e0b",
    "1xbet": "#ef4444",
    Snapchat: "#10b981",
    GoldPari: "#FF0000",
  };

  const datasets = apps.map((app) => ({
    label: app,
    data: dates.map((date) => groupedData[date]?.[app] || 0),
    backgroundColor: appColors[app] || "#38bdf8",
  }));

  const chartData = {
    labels: dates,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw} OTPs`,
        },
      },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
  };

  return (
    <div className="appwise-count-v2">
      <div className="appwise-count-v2-header">
        <h1 className="appwise-count-v2-title">
          App Usage Breakdown: Special Testers
        </h1>
      </div>

      <div className="appwise-count-v2-content">
        <div className="tester-stats-date-picker-wrap appwise-count-v2-picker">
          <DateRangePicker
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            months={1}
            ranges={[
              {
                startDate: selectedRange[0],
                endDate: selectedRange[1],
                key: "selection",
              },
            ]}
            rangeColors={["#38bdf8"]}
            color="#38bdf8"
          />
        </div>

        <div className="appwise-count-v2-chart-card">
          {loading ? (
            <div className="appwise-count-v2-loading">
              <Spin size="large" />
            </div>
          ) : data.length > 0 ? (
            <div className="appwise-count-v2-chart-wrap">
              <Bar data={chartData} options={options} />
            </div>
          ) : (
            <div className="appwise-count-v2-empty">No data available.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppWiseCountV2;
